import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { supabase } from '../../../utils/supabase';
import ImageWithFallback from '../../../components/ImageWithFallback';
import { createCompanySlug } from '../../../utils/companySlugUtils';

const CompanyProfileModal = ({ isOpen, onClose, employer, reviews, averageRating, reviewCount }) => {
  if (!isOpen) return null;

  // Helper function to parse company_culture which can be either string or array
  const parseCompanyCulture = (culture) => {
    if (!culture) return [];
    if (Array.isArray(culture)) return culture;
    try {
      return JSON.parse(culture);
    } catch (e) {
      return [];
    }
  };
  
  // Helper function for safely getting array fields
  const getArrayField = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    try {
      return JSON.parse(field);
    } catch (e) {
      return [];
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <span 
        key={i} 
        className={`text-lg ${i < Math.round(rating) ? 'text-yellow-400' : 'text-gray-300'}`}
      >
        ★
      </span>
    ));
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-surface rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-bold text-text-primary">Company Profile</h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
          >
            <Icon name="X" size={20} className="text-text-muted" />
          </button>
        </div>

        {/* Company Banner */}
        <div className="w-full h-48 md:h-64 overflow-hidden">
          <ImageWithFallback
            src={employer?.company_banner}
            fallbackSrc="/images/fallback-company-banner.png"
            alt={`${employer?.company_name || 'Company'} banner`}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Modal Content */}
        <div className="p-6 space-y-8">
          {/* Company Header */}
          <div className="flex items-center space-x-6">
            <div className="w-40 h-40 rounded-lg overflow-hidden bg-secondary-100 flex-shrink-0">
              <ImageWithFallback
                src={employer?.company_logo || employer?.profile_picture}
                fallbackSrc="/assets/images/Logo/webp/VAS-LOGO.webp"
                alt={employer?.company_name || 'Company'}
                className="w-full h-full object-contain"
              />
            </div>
            <div className="flex-1">
              <h3 className="text-2xl font-bold text-text-primary mb-2">
                {employer?.company_name || employer?.full_name}
              </h3>
              {/* Company Description */}
          {(employer?.bio || employer?.company_description) && (
            <div>
              <p className="text-text-secondary leading-relaxed">
                {employer.bio || employer.company_description}
              </p>
            </div>
          )}
              <div className="flex items-center space-x-4 text-text-secondary mt-3 mb-3">
                {employer?.industry && (
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                    {employer.industry}
                  </span>
                )}
                {employer?.company_size && (
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                    {employer.company_size} employees
                  </span>
                )}
              </div>
              
              {/* Rating Display */}
              {reviewCount > 0 && (
                <div className="flex items-center space-x-3">
                  <div className="flex">{renderStars(averageRating)}</div>
                  <span className="text-text-secondary">
                    {averageRating.toFixed(1)} out of 5 ({reviewCount} reviews)
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Company Details Grid */}
          <div className="grid md:grid-cols-2 gap-8">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Basic Info */}
              <div>
                <h4 className="text-lg font-semibold text-text-primary mb-4">Company Information</h4>
                <div className="space-y-3">
                  {employer?.location && (
                    <div className="flex items-center">
                      <Icon name="MapPin" size={18} className="text-text-muted mr-3" />
                      <span className="text-text-secondary">{employer.location}</span>
                    </div>
                  )}
                  
                  {employer?.founded_year && (
                    <div className="flex items-center">
                      <Icon name="Calendar" size={18} className="text-text-muted mr-3" />
                      <span className="text-text-secondary">Founded {employer.founded_year}</span>
                    </div>
                  )}

                  {(employer?.time_zone || employer?.timezone) && (
                    <div className="flex items-center">
                      <Icon name="Clock" size={18} className="text-text-muted mr-3" />
                      <span className="text-text-secondary">{employer.time_zone || employer.timezone}</span>
                    </div>
                  )}

                  {employer?.website && (
                    <div className="flex items-center">
                      <Icon name="Globe" size={18} className="text-text-muted mr-3" />
                      <a 
                        href={employer.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {employer.website}
                      </a>
                    </div>
                  )}

                  {employer?.hiring_budget_range && (
                    <div className="flex items-center">
                      <Icon name="DollarSign" size={18} className="text-text-muted mr-3" />
                      <span className="text-text-secondary">{employer.hiring_budget_range}</span>
                    </div>
                  )}

                  {employer?.languages_spoken && (
                    <div className="flex items-start">
                      <Icon name="MessageCircle" size={18} className="text-text-muted mr-3 mt-0.5" />
                      <span className="text-text-secondary">
                        {getArrayField(employer.languages_spoken).join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Company Culture */}
              <div>
                <h4 className="text-lg font-semibold text-text-primary mb-4">Company Culture</h4>
                {employer?.company_culture ? (
                    <div className="flex flex-wrap gap-2">
                      {getArrayField(employer.company_culture).length > 0 ? (
                        getArrayField(employer.company_culture).map((value, index) => (
                          <span 
                            key={index}
                            className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm"
                          >
                            {value}
                          </span>
                        ))
                      ) : (
                        <span className="text-text-secondary">No company culture information available</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-text-secondary">No company culture information available</span>
                  )
                }
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Benefits */}
              {employer?.benefits && getArrayField(employer.benefits).length > 0 ? (
                <div>
                  <h4 className="text-lg font-semibold text-text-primary mb-4">Benefits & Perks</h4>
                  <div className="space-y-2">
                    {getArrayField(employer.benefits).map((benefit, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <Icon name="Check" size={16} className="text-green-500 flex-shrink-0" />
                        <span className="text-text-secondary">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* Skills Looking For */}
              <div>
                <h4 className="text-lg font-semibold text-text-primary mb-4">Skills We Value</h4>
                {employer?.preferred_skills ? (
                  <div className="flex flex-wrap gap-2">
                    {getArrayField(employer.preferred_skills).length > 0 ? (
                      getArrayField(employer.preferred_skills).map((skill, index) => (
                        <span 
                          key={index}
                          className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm"
                        >
                          {skill}
                        </span>
                      ))
                    ) : (
                      <span className="text-text-secondary">No specific skills listed</span>
                    )}
                  </div>
                ) : (
                  <span className="text-text-secondary">No specific skills listed</span>
                )}
              </div>

              {/* Recent Reviews */}
              {reviews && reviews.length > 0 && (
                <div>
                  <h4 className="text-lg font-semibold text-text-primary mb-4">Recent Reviews</h4>
                  <div className="space-y-4">
                    {reviews.slice(0, 3).map((review, index) => (
                      <div key={index} className="bg-secondary-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                              <Icon name="User" size={14} className="text-primary" />
                            </div>
                            <span className="text-sm font-medium text-text-primary">
                              {review.reviewer?.full_name || 'Anonymous'}
                            </span>
                          </div>
                          <div className="flex">
                            {renderStars(review.rating)}
                          </div>
                        </div>
                        {review.comment && (
                          <p className="text-sm text-text-secondary">
                            {review.comment}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Contact Actions */}
          <div className="flex space-x-4 pt-6 border-t border-border">
            <Link 
              to={`/job-search?company=${createCompanySlug(employer)}`}
              className="flex-1 bg-secondary-100 text-text-primary rounded-lg py-3 px-6 font-medium hover:bg-secondary-200 transition-colors text-center flex items-center justify-center"
            >
              <Icon name="Briefcase" size={18} className="mr-2" />
              View All Jobs
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const CompanyInfo = ({ employer }) => {
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);
  const [_loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  
  // Helper function to parse company_culture which can be either string or array
  const parseCompanyCulture = (culture) => {
    if (!culture) return [];
    if (Array.isArray(culture)) return culture;
    try {
      return JSON.parse(culture);
    } catch (e) {
      return [];
    }
  };
  
  // Helper function for safely getting array fields
  const getArrayField = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    try {
      return JSON.parse(field);
    } catch (e) {
      return [];
    }
  };

  const fetchCompanyReviews = useCallback(async () => {
    if (!employer?.id) {
      return;
    }
    
    try {
      setLoading(true);
      
      const { data: reviewsData, error } = await supabase
        .from('company_reviews')
        .select(`
          *,
          reviewer:reviewer_id(full_name, profile_picture)
        `)
        .eq('company_id', employer.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) {
        return;
      }
      
      if (reviewsData) {
        setReviews(reviewsData);
        setReviewCount(reviewsData.length);
        
        // Calculate average rating
        if (reviewsData.length > 0) {
          const avg = reviewsData.reduce((acc, review) => acc + review.rating, 0) / reviewsData.length;
          setAverageRating(avg);
        }
      }
    } catch (error) {
      // Error handling
    } finally {
      setLoading(false);
    }
  }, [employer?.id]);

  useEffect(() => {
    fetchCompanyReviews();
  }, [fetchCompanyReviews]);

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <span 
        key={i} 
        className={`text-sm ${i < Math.round(rating) ? 'text-yellow-400' : 'text-gray-300'}`}
      >
        ★
      </span>
    ));
  };

  // State for storing employer data that might need enhancement
  const [employerData, setEmployerData] = useState(employer);

  useEffect(() => {
    if (employer) {
      // Update the local state whenever the employer prop changes
      setEmployerData(employer);
      
      // If employer ID exists and we need to get more details, fetch them
      if (employer.id) {
        
        const fetchEmployerDetails = async () => {
          try {
            const { data, error } = await supabase
              .from('user_profiles')
              .select('*')
              .eq('id', employer.id)
              .single();
              
            if (error) {
              return;
            }
            
            if (data) {
              // Merge fetched data with existing employer data
              setEmployerData(prevData => ({
                ...prevData,
                ...data
              }));
            }
          } catch (err) {
            // Error handling
          }
        };
        
        fetchEmployerDetails();
      }
    }
  }, [employer]);

  if (!employerData) {
    return null;
  }

  return (
    <div className="bg-surface rounded-lg border border-border p-6">
      <h3 className="text-lg font-bold text-text-primary mb-4 flex items-center">
        <Icon name="Building2" size={18} className="text-primary mr-2" />
        About the Company
      </h3>

      <div className="space-y-4">
        {/* Company Header */}
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 rounded-lg overflow-hidden bg-secondary-100">
            <ImageWithFallback
                src={employer?.company_logo || employer?.profile_picture}
                fallbackSrc="/assets/images/Logo/webp/VAS-LOGO.webp"
                alt={employer?.company_name || 'Company'}
                className="w-full h-full object-contain"
                                          />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-text-primary">
              {employer?.company_name || employer?.full_name}
            </h4>
            <div className="flex items-center space-x-2 text-sm text-text-secondary">
              {employer?.industry && (
                <span>{employer.industry}</span>
              )}
              {employer?.company_size && employer?.industry && (
                <span>•</span>
              )}
              {employer?.company_size && (
                <span>{employer.company_size} employees</span>
              )}
            </div>
            {employer?.location && (
              <p className="text-sm text-text-secondary">{employer.location}</p>
            )}
            
            {/* Rating Display */}
            {reviewCount > 0 && (
              <div className="flex items-center mt-2">
                <div className="flex">{renderStars(averageRating)}</div>
                <span className="ml-2 text-sm text-text-secondary">
                  {averageRating.toFixed(1)} ({reviewCount} reviews)
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Company Description */}
        {(employer?.bio || employer?.company_description) && (
          <div>
            <p className="text-sm text-text-secondary leading-relaxed">
              {employer.bio || employer.company_description}
            </p>
          </div>
        )}

        {/* Company Culture */}
        <div>
          <h5 className="font-medium text-text-primary mb-2">Company Culture</h5>
          {employerData?.company_culture ? (
              <div className="flex flex-wrap gap-1">
                {parseCompanyCulture(employerData.company_culture).length > 0 ? (
                  <>
                    {parseCompanyCulture(employerData.company_culture).slice(0, 4).map((value, index) => (
                      <span 
                        key={index}
                        className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs"
                      >
                        {value}
                      </span>
                    ))}
                    {parseCompanyCulture(employerData.company_culture).length > 4 && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                        +{parseCompanyCulture(employerData.company_culture).length - 4} more
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-text-secondary text-xs">No company culture information available</span>
                )}
              </div>
            ) : (
              <span className="text-text-secondary text-xs">No company culture information available</span>
            )}
        </div>

        {/* Benefits Preview */}
        {employerData?.benefits && getArrayField(employerData.benefits).length > 0 && (
            <div>
              <h5 className="font-medium text-text-primary mb-2">Benefits</h5>
              <div className="space-y-1">
                {getArrayField(employerData.benefits).slice(0, 3).map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <Icon name="Check" size={12} className="text-green-500" />
                    <span className="text-xs text-text-secondary">{benefit}</span>
                  </div>
                ))}
                {getArrayField(employerData.benefits).length > 3 && (
                  <p className="text-xs text-text-muted">
                    +{getArrayField(employerData.benefits).length - 3} more benefits
                  </p>
                )}
              </div>
            </div>
        )}

        {/* Company Details */}
        <div className="space-y-3 pt-4 border-t border-border">
          {employer?.location && (
            <div className="flex items-center">
              <Icon name="MapPin" size={16} className="text-text-muted mr-3" />
              <span className="text-sm text-text-secondary">{employer.location}</span>
            </div>
          )}

          {(employer?.time_zone || employer?.timezone) && (
            <div className="flex items-center">
              <Icon name="Clock" size={16} className="text-text-muted mr-3" />
              <span className="text-sm text-text-secondary">{employer.time_zone || employer.timezone}</span>
            </div>
          )}

          {employer?.hiring_budget_range && (
            <div className="flex items-center">
              <Icon name="DollarSign" size={16} className="text-text-muted mr-3" />
              <span className="text-sm text-text-secondary">{employer.hiring_budget_range}</span>
            </div>
          )}

          {employer?.founded_year && (
            <div className="flex items-center">
              <Icon name="Calendar" size={16} className="text-text-muted mr-3" />
              <span className="text-sm text-text-secondary">
                Founded {employer.founded_year}
              </span>
            </div>
          )}

          {employer?.website && (
            <div className="flex items-center">
              <Icon name="Globe" size={16} className="text-text-muted mr-3" />
              <a 
                href={employer.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline"
              >
                Visit Website
              </a>
            </div>
          )}

          {employer?.languages_spoken && (
            <div className="flex items-center">
              <Icon name="MessageCircle" size={16} className="text-text-muted mr-3" />
              <span className="text-sm text-text-secondary">
                {getArrayField(employer.languages_spoken).join(', ')}
              </span>
            </div>
          )}

          <div className="flex items-center">
            <Icon name="Calendar" size={16} className="text-text-muted mr-3" />
            <span className="text-sm text-text-secondary">
              Member since {new Date(employer?.created_at || Date.now()).getFullYear()}
            </span>
          </div>
        </div>

        {/* Contact Button */}
        <div className="pt-4 border-t border-border">
          <button 
            onClick={() => setShowModal(true)}
            className="w-full bg-primary-50 hover:bg-primary-100 text-primary border border-primary-200 rounded-lg py-2 px-4 text-sm font-medium transition-colors"
          >
            View Company Profile
          </button>
        </div>
      </div>

      {/* Company Profile Modal */}
      <CompanyProfileModal 
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        employer={employerData}
        reviews={reviews}
        averageRating={averageRating}
        reviewCount={reviewCount}
      />
    </div>
  );
};

export default CompanyInfo;
